import { useState, useCallback, useEffect } from 'react'
import { useTranslation } from '../../contexts/LanguageContext'
import { Input } from '../ui/Input'
import { Textarea } from '../ui/Textarea'
import { Button } from '../ui/Button'
import { SortableList, SortableItem, DragHandle } from '../ui/SortableList'
import { CVLabelInput } from './CVLabelInput'
import type { Experience } from '../../types/cv'

interface ExperienceFormProps {
  data: Experience[] | undefined
  onAdd: () => void
  onUpdate: (id: string, data: Partial<Experience>) => void
  onRemove: (id: string) => void
  onReorder?: (oldIndex: number, newIndex: number) => void
  customLabel?: string
  onCustomLabelChange: (value: string) => void
}

export function ExperienceForm({
  data,
  onAdd,
  onUpdate,
  onRemove,
  onReorder,
  customLabel,
  onCustomLabelChange,
}: ExperienceFormProps) {
  const { t } = useTranslation()
  const [openId, setOpenId] = useState<string | null>(null)

  // Open newly added experience
  useEffect(() => {
    if (data && data.length > 0) {
      const lastExp = data[data.length - 1]
      if (!lastExp.company && !lastExp.position) {
        setOpenId(lastExp.id)
      }
    }
  }, [data?.length])

  const handleChange = useCallback(
    (id: string, field: keyof Experience) =>
      (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        onUpdate(id, { [field]: e.target.value })
      },
    [onUpdate]
  )

  const handleCurrentChange = useCallback(
    (id: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      onUpdate(id, {
        current: e.target.checked,
        endDate: e.target.checked ? undefined : '',
      })
    },
    [onUpdate]
  )

  const toggleExpand = (id: string) => {
    setOpenId(openId === id ? null : id)
  }

  const getTitle = (exp: Experience, index: number) => {
    if (exp.company || exp.position) {
      const parts = [exp.position, exp.company].filter(Boolean)
      return parts.join(' - ') || `${t.experienceForm.itemTitle} ${index + 1}`
    }
    return `${t.experienceForm.itemTitle} ${index + 1}`
  }

  return (
    <div className="space-y-2">
      <CVLabelInput value={customLabel} placeholder="Experience" onChange={onCustomLabelChange} />
      {(!data || data.length === 0) && (
        <p className="text-sm text-slate-500 italic">
          {t.experienceForm.empty}
        </p>
      )}

      <SortableList
        items={(data || []).map(exp => exp.id)}
        onReorder={onReorder || (() => {})}
      >
        {(data || []).map((exp, index) => {
          const isOpen = openId === exp.id
          return (
            <SortableItem key={exp.id} id={exp.id}>
              {({ dragHandleProps, isDragging }) => (
                <div
                  className={`border border-slate-200 rounded-lg overflow-hidden ${isDragging ? 'shadow-lg' : ''}`}
                >
                  {/* Header */}
                  <div
                    className={`flex items-center p-3 transition-colors ${
                      isOpen ? 'bg-slate-50' : 'bg-white hover:bg-slate-50'
                    }`}
                  >
                    {onReorder && <DragHandle {...dragHandleProps} />}
                    <button
                      type="button"
                      onClick={() => toggleExpand(exp.id)}
                      className="flex-1 flex items-center justify-between text-left min-w-0"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                          exp.company || exp.position ? 'bg-green-500' : 'bg-slate-300'
                        }`} />
                        <span className="font-medium text-slate-900 truncate">
                          {getTitle(exp, index)}
                        </span>
                      </div>
                      <svg
                        className={`w-5 h-5 text-slate-400 flex-shrink-0 transition-transform ${
                          isOpen ? 'rotate-180' : ''
                        }`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </button>
                  </div>

                  {/* Content */}
                  {isOpen && (
                    <div className="p-4 border-t border-slate-200 bg-white space-y-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Input
                          label={t.experienceForm.company}
                          placeholder={t.experienceForm.companyPlaceholder}
                          value={exp.company || ''}
                          onChange={handleChange(exp.id, 'company')}
                        />
                        <Input
                          label={t.experienceForm.position}
                          placeholder={t.experienceForm.positionPlaceholder}
                          value={exp.position || ''}
                          onChange={handleChange(exp.id, 'position')}
                        />
                      </div>

                      <Input
                        label={t.experienceForm.location}
                        placeholder={t.experienceForm.locationPlaceholder}
                        value={exp.location || ''}
                        onChange={handleChange(exp.id, 'location')}
                      />

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Input
                          label={t.experienceForm.startDate}
                          type="month"
                          value={exp.startDate || ''}
                          onChange={handleChange(exp.id, 'startDate')}
                        />
                        <div>
                          <Input
                            label={t.experienceForm.endDate}
                            type="month"
                            value={exp.endDate || ''}
                            onChange={handleChange(exp.id, 'endDate')}
                            disabled={exp.current}
                          />
                          <label className="flex items-center gap-2 mt-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={exp.current || false}
                              onChange={handleCurrentChange(exp.id)}
                              className="w-4 h-4 text-primary border-slate-300 rounded focus:ring-primary"
                            />
                            <span className="text-sm text-slate-600">
                              {t.experienceForm.currentJob}
                            </span>
                          </label>
                        </div>
                      </div>

                      <Textarea
                        label={t.experienceForm.description}
                        placeholder={t.experienceForm.descriptionPlaceholder}
                        value={exp.description || ''}
                        onChange={handleChange(exp.id, 'description')}
                        rows={3}
                      />

                      <div className="pt-2 border-t border-slate-100">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => onRemove(exp.id)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          {t.experienceForm.delete}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </SortableItem>
          )
        })}
      </SortableList>

      <Button type="button" variant="secondary" onClick={onAdd} className="w-full">
        {t.experienceForm.add}
      </Button>
    </div>
  )
}
